/**
 * Universal UTF-8 Mojibake Repair Utility
 * 
 * When UTF-8 text is read through a single-byte encoding (CP437, Windows-1252, etc.),
 * multi-byte characters turn into garbled sequences. This module reverses that by
 * building a reverse lookup table from CP437 Unicode code points back to their
 * original byte values, then re-decoding the bytes as UTF-8.
 *
 * Example: "SIMcaf├⌐" → bytes [S,I,M,c,a,f,0xC3,0xA9] → UTF-8 decode → "SIMcafé"
 */

// CP437 byte→Unicode mapping for bytes 0x80–0xFF
const CP437_TO_UNICODE = [
  // 0x80-0x8F
  0x00C7, 0x00FC, 0x00E9, 0x00E2, 0x00E4, 0x00E0, 0x00E5, 0x00E7,
  0x00EA, 0x00EB, 0x00E8, 0x00EF, 0x00EE, 0x00EC, 0x00C4, 0x00C5,
  // 0x90-0x9F
  0x00C9, 0x00E6, 0x00C6, 0x00F4, 0x00F6, 0x00F2, 0x00FB, 0x00F9,
  0x00FF, 0x00D6, 0x00DC, 0x00A2, 0x00A3, 0x00A5, 0x20A7, 0x0192,
  // 0xA0-0xAF
  0x00E1, 0x00ED, 0x00F3, 0x00FA, 0x00F1, 0x00D1, 0x00AA, 0x00BA,
  0x00BF, 0x2310, 0x00AC, 0x00BD, 0x00BC, 0x00A1, 0x00AB, 0x00BB,
  // 0xB0-0xBF
  0x2591, 0x2592, 0x2593, 0x2502, 0x2524, 0x2561, 0x2562, 0x2556,
  0x2555, 0x2563, 0x2551, 0x2557, 0x255D, 0x255C, 0x255B, 0x2510,
  // 0xC0-0xCF
  0x2514, 0x2534, 0x252C, 0x251C, 0x2500, 0x253C, 0x255E, 0x255F,
  0x255A, 0x2554, 0x2569, 0x2566, 0x2560, 0x2550, 0x256C, 0x2567,
  // 0xD0-0xDF
  0x2568, 0x2564, 0x2565, 0x2559, 0x2558, 0x2552, 0x2553, 0x256B,
  0x256A, 0x2518, 0x250C, 0x2588, 0x2584, 0x258C, 0x2590, 0x2580,
  // 0xE0-0xEF
  0x03B1, 0x00DF, 0x0393, 0x03C0, 0x03A3, 0x03C3, 0x00B5, 0x03C4,
  0x03A6, 0x0398, 0x03A9, 0x03B4, 0x221E, 0x03C6, 0x03B5, 0x2229,
  // 0xF0-0xFF
  0x2261, 0x00B1, 0x2265, 0x2264, 0x2320, 0x2321, 0x00F7, 0x2248,
  0x00B0, 0x2219, 0x00B7, 0x221A, 0x207F, 0x00B2, 0x25A0, 0x00A0,
];

// Build reverse map: Unicode code point → CP437 byte value
const UNICODE_TO_CP437 = new Map();
for (let byte = 0; byte < CP437_TO_UNICODE.length; byte++) {
  UNICODE_TO_CP437.set(CP437_TO_UNICODE[byte], byte + 0x80);
}

// Characters from CP437 high range that are very unlikely in normal session text
// (box-drawing, math symbols, Greek letters used as math, block elements)
const SUSPICIOUS_CP437 = new Set([
  0x2591, 0x2592, 0x2593, // ░▒▓
  0x2502, 0x2524, 0x2561, 0x2562, 0x2556, 0x2555, 0x2563, 0x2551, // │┤╡╢╖╕╣║
  0x2557, 0x255D, 0x255C, 0x255B, 0x2510, 0x2514, 0x2534, 0x252C, // ╗╝╜╛┐└┴┬
  0x251C, 0x2500, 0x253C, 0x255E, 0x255F, 0x255A, 0x2554, 0x2569, // ├─┼╞╟╚╔╩
  0x2566, 0x2560, 0x2550, 0x256C, 0x2567, 0x2568, 0x2564, 0x2565, // ╦╠═╬╧╨╤╥
  0x2559, 0x2558, 0x2552, 0x2553, 0x256B, 0x256A, 0x2518, 0x250C, // ╙╘╒╓╫╪┘┌
  0x2588, 0x2584, 0x258C, 0x2590, 0x2580, // █▄▌▐▀
  0x03B1, 0x0393, 0x03C0, 0x03A3, 0x03C3, 0x03C4, 0x03A6, 0x0398, // αΓπΣστΦΘ
  0x03A9, 0x03B4, 0x03C6, 0x03B5, // Ωδφε
  0x221E, 0x2229, 0x2261, 0x2265, 0x2264, 0x2320, 0x2321, 0x2248, // ∞∩≡≥≤⌠⌡≈
  0x2219, 0x221A, 0x25A0, 0x2310, // ∙√■⌐
  0x0192, 0x20A7, // ƒ₧
]);

/**
 * Checks if a string likely contains CP437 mojibake by looking for
 * "suspicious" characters that wouldn't appear in normal text.
 */
function hasSuspiciousChars(str) {
  for (let i = 0; i < str.length; i++) {
    if (SUSPICIOUS_CP437.has(str.charCodeAt(i))) return true;
  }
  return false;
}

/**
 * Tries to reverse CP437 mojibake by mapping Unicode code points back
 * to their CP437 byte values, then decoding the resulting bytes as UTF-8.
 */
function tryFixCP437(str) {
  const bytes = [];
  for (let i = 0; i < str.length; i++) {
    const cp = str.charCodeAt(i);
    if (cp < 0x80) {
      // ASCII range: same in both encodings
      bytes.push(cp);
    } else {
      const byte = UNICODE_TO_CP437.get(cp);
      if (byte !== undefined) {
        bytes.push(byte);
      } else if (cp <= 0xFF) {
        // Latin-1 supplementary range: use code point as byte directly
        bytes.push(cp);
      } else {
        // Can't reverse this character — not from CP437
        return null;
      }
    }
  }

  try {
    const decoder = new TextDecoder('utf-8', { fatal: true });
    return decoder.decode(new Uint8Array(bytes));
  } catch {
    return null; // Bytes don't form valid UTF-8 — not mojibake
  }
}

/**
 * Tries to reverse Windows-1252 mojibake (the other common case).
 * Windows-1252 bytes 0x80-0x9F map to special characters; 0xA0-0xFF match Latin-1.
 */
const WIN1252_SPECIALS = {
  0x20AC: 0x80, // €
  0x201A: 0x82, // ‚
  0x0192: 0x83, // ƒ
  0x201E: 0x84, // „
  0x2026: 0x85, // …
  0x2020: 0x86, // †
  0x2021: 0x87, // ‡
  0x02C6: 0x88, // ˆ
  0x2030: 0x89, // ‰
  0x0160: 0x8A, // Š
  0x2039: 0x8B, // ‹
  0x0152: 0x8C, // Œ
  0x017D: 0x8E, // Ž
  0x2018: 0x91, // '
  0x2019: 0x92, // '
  0x201C: 0x93, // "
  0x201D: 0x94, // "
  0x2022: 0x95, // •
  0x2013: 0x96, // –
  0x2014: 0x97, // —
  0x02DC: 0x98, // ˜
  0x2122: 0x99, // ™
  0x0161: 0x9A, // š
  0x203A: 0x9B, // ›
  0x0153: 0x9C, // œ
  0x017E: 0x9E, // ž
  0x0178: 0x9F, // Ÿ
};

function tryFixWin1252(str) {
  const bytes = [];
  for (let i = 0; i < str.length; i++) {
    const cp = str.charCodeAt(i);
    if (cp < 0x80) {
      bytes.push(cp);
    } else if (cp <= 0xFF) {
      bytes.push(cp); // Latin-1 range maps directly
    } else {
      const byte = WIN1252_SPECIALS[cp];
      if (byte !== undefined) {
        bytes.push(byte);
      } else {
        return null; // Not a Win-1252 character
      }
    }
  }

  try {
    const decoder = new TextDecoder('utf-8', { fatal: true });
    return decoder.decode(new Uint8Array(bytes));
  } catch {
    return null;
  }
}

/**
 * Universal encoding repair function. Detects and fixes mojibake from
 * CP437 and Windows-1252 misinterpretation of UTF-8 text.
 * 
 * Safe: only modifies text if the reversal produces valid UTF-8.
 * If the string is already clean, it's returned unchanged.
 * 
 * @param {string} str - The potentially garbled string
 * @returns {string} - The repaired string, or the original if no fix needed
 */
export function fixEncoding(str) {
  if (!str || typeof str !== 'string') return str;

  // Fast path: pure ASCII needs no repair
  let hasNonAscii = false;
  for (let i = 0; i < str.length; i++) {
    if (str.charCodeAt(i) > 0x7F) { hasNonAscii = true; break; }
  }
  if (!hasNonAscii) return str;

  // Strategy 1: Try CP437 reversal (if suspicious box-drawing/math chars present)
  if (hasSuspiciousChars(str)) {
    const fixed = tryFixCP437(str);
    if (fixed && fixed !== str && fixed.length <= str.length) {
      return fixed;
    }
  }

  // Strategy 2: Try Windows-1252 reversal (Ã© → é pattern)
  if (str.includes('\u00C3') || str.includes('\u00E2') || str.includes('\u00C2')) {
    const fixed = tryFixWin1252(str);
    if (fixed && fixed !== str && fixed.length <= str.length) {
      return fixed;
    }
  }

  return str;
}

/**
 * Apply fixEncoding to all string values in a session object.
 * Non-string values and keys are left untouched.
 * 
 * @param {Object} session - Session object from the API
 * @returns {Object} - New session object with all strings repaired
 */
export function fixSessionEncoding(session) {
  if (!session || typeof session !== 'object') return session;
  const fixed = { ...session };
  for (const key of Object.keys(fixed)) {
    if (typeof fixed[key] === 'string') {
      fixed[key] = fixEncoding(fixed[key]);
    }
  }
  return fixed;
}

export default fixEncoding;
